import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
} from "../ui/skeleton";
import { Alert, AlertDescription } from "../ui/alert";
import { Shield, User, Calendar, Building2 } from "lucide-react";
import { getApiUrl } from "../../config/api";

const MyAssignedControls = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMyAssignedControls = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("controls/my-assigned"), {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch assigned controls");
      }

      const data = await response.json();
      setControls(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyAssignedControls();
  }, [getAccessTokenSilently]);

  const getControlStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "pending":
        return "warning";
      default:
        return "outline";
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Header Skeleton */}
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Table Skeleton */}
        <SkeletonCard>
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <SkeletonTable rows={5} columns={5} />
          </div>
        </SkeletonCard>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">My Assigned Controls</h1>
        <p className="text-muted-foreground">
          These are the controls that have been assigned to you for
          implementation and monitoring.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Total Assigned</p>
              <p className="text-2xl font-bold text-foreground">
                {controls.length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Active Controls</p>
              <p className="text-2xl font-bold text-foreground">
                {
                  controls.filter((c) => c.status?.toLowerCase() === "active")
                    .length
                }
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-center p-6">
            <div className="text-center">
              <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Pending Controls</p>
              <p className="text-2xl font-bold text-foreground">
                {
                  controls.filter((c) => c.status?.toLowerCase() === "pending")
                    .length
                }
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls List */}
      {controls.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Controls Assigned</h3>
            <p className="text-muted-foreground">
              You don't have any controls assigned to you yet. Contact your
              business unit head for assignments.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {controls.map((control) => (
            <Card key={control.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      {control.name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {control.description}
                    </p>
                  </div>
                  <Badge variant={getControlStatusColor(control.status)}>
                    {control.status || "Unknown"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Business Unit:
                    </span>
                    <span className="font-medium">
                      {control.business_unit_name || "Organization Level"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Created by:</span>
                    <span className="font-medium">
                      {control.creator_name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Created:</span>
                    <span className="font-medium">
                      {new Date(control.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {(control.categories?.length > 0 ||
                  control.category ||
                  control.Category) && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {control.categories?.length > 0 ? (
                        control.categories.map((category) => (
                          <Badge
                            key={category.id}
                            variant="outline"
                            className="text-xs"
                          >
                            {category.name}
                            {category.sources?.some(
                              (s) => s.type === "auto_risk_association"
                            ) && " (Auto)"}
                          </Badge>
                        ))
                      ) : control.Category ? (
                        <Badge variant="outline">{control.Category.name}</Badge>
                      ) : control.category ? (
                        <Badge variant="outline">{control.category}</Badge>
                      ) : null}
                    </div>
                  </div>
                )}

                {control.associated_risks &&
                  control.associated_risks.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium mb-2">
                        Associated Risks:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {control.associated_risks.map((risk) => (
                          <Badge
                            key={risk.id}
                            variant="secondary"
                            className="text-xs"
                          >
                            {risk.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAssignedControls;
